/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// models
import Model from "models/model/Model.js";

// utils
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// constants
const METADATA_DEFAULT = { "content-type": "image/*" };
const SOURCES_DEFAULT = {
  full: String("not-set"),
  large: String("not-set"),
  medium: String("not-set"),
  small: String("not-set"),
  thumb: String("not-set"),
};
const TITLE_DEFAULT = String("Unkown");

class Image extends Model {
  constructor({
    alt = String(""),
    id = Model.getDefaultId(),
    lastUpdated = Model.getDefaultLastUpdated(),
    metadata = { ...METADATA_DEFAULT },
    sources = { ...SOURCES_DEFAULT },
    tags = [{ model: "Image" }],
    title = String(TITLE_DEFAULT),
  } = {}) {

    // state
    super({ id, lastUpdated, tags });
    this.state = {
      ...this.state,
      alt,
      metadata,
      sources,
      title,
    };

    // setters
    this.setAlt(alt);
    this.setMetadata(metadata);
    this.setSources(sources);
    this.setTitle(title);

    // front-end imageUrls
    this.urls = { ...sources };
  }

  /*====================
  getters
  ====================*/
  getAlt() { return String(this.state.alt); }
  getMetadata() { return { ...this.state.images }; }
  getSources() { return { ...this.state.sources }; }
  getTitle() { return String(this.state.title); }

  getState() {
    return {
      ...this.getModelState(),
      alt: this.getAlt(),
      metadata: this.getMetadata(),
      sources: this.getSources(),
      tags: this.getTags(),
      title: this.getTitle(),
    };
  }

  /*====================
  setters
  ====================*/
  /**
  * @param {String} alt
  * @returns {String} alt
  */
  setAlt(alt) {
    if(ValidationUtils.string(alt)) {
      this.state.alt = String(alt);
      this.onChange();
    }
    else if(String(alt).trim() === String("")) {
      this.state.alt = "";
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (string) setAlt.", alt);
    }
    return this.getAlt();
  }

  /**
  * @param {Object} metadata
  * @returns {Object} metadata
  */
  setMetadata(metadata) {
    if(ValidationUtils.exists(metadata)) {
      this.state.metadata = { ...metadata };
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (exists) setMetadata.", metadata);
    }
    return this.getMetadata();
  }

  /**
  * @param {Object} sources
  * @returns {Object} sources
  */
  setSources({
    full = "not-set",
    large = "not-set",
    medium = "not-set",
    small = "not-set",
    thumb = "not-set",
  }) {
    const sources = {
      full,
      large,
      medium,
      small,
      thumb,
    };
    if(ValidationUtils.imageSources(sources)) {
      this.state.sources = { ...sources };
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (imageSources) setSources.", sources);
    }
    return this.getSources();
  }

  setTitle(string) {
    if(ValidationUtils.string(string)) {
      this.state.title = String(string);
      this.onChange();
    }
    else if(String(string).trim() === String("")) {
      this.state.title = "";
      this.onChange();
    }
    else {
      LoggerUtils.error("Unable to validate (string) setTitle.", string);
    }
    return this.getTitle();
  }

  /*====================
  validation
  ====================*/
  validate() { // NOSONAR
    const state = this.getState();

    // property checks
    // ====================
    if(!state.hasOwnProperty("alt"))
    { throw Error(`Missing property "alt".`); }

    else if(!state.hasOwnProperty("id"))
    { throw Error(`Missing property "id".`); }

    else if(!state.hasOwnProperty("lastUpdated"))
    { throw Error(`Missing property "lastUpdated".`); }

    else if(!state.hasOwnProperty("metadata"))
    { throw Error(`Missing property "metadata".`); }

    else if(!state.hasOwnProperty("sources"))
    { throw Error(`Missing property "sources".`); }

    else if(!state.hasOwnProperty("tags"))
    { throw Error(`Missing property "tags".`); }

    else if(!state.hasOwnProperty("title"))
    { throw Error(`Missing property "title".`); }

    // value checks
    // ====================
    const alt = this.getAlt();
    const id = this.getId();
    const lastUpdated = this.getLastUpdated();
    const metadata = this.getMetadata();
    const sources = this.getSources();
    const tags = this.getTags();
    const title = this.getTitle();

    if(!ValidationUtils.string(alt))
    { throw Error(`Invalid property "alt" does not pass validation "string": ${alt}`); }

    else if(!ValidationUtils.string(id))
    { throw Error(`Invalid property "id" does not pass validation "string": ${id}`); }

    else if(!ValidationUtils.string(lastUpdated))
    { throw Error(`Invalid property "lastUpdated" does not pass validation "string": ${lastUpdated}`); }

    else if(!ValidationUtils.exists(metadata))
    { throw Error(`Invalid property "metadata" does not pass validation "exists": ${metadata}`); }

    else if(!ValidationUtils.imageSources(sources))
    { throw Error(`Invalid property "sources" does not pass validation "imageSources": ${sources}`); }

    else if(!ValidationUtils.array(tags))
    { throw Error(`Invalid property "tags" does not pass validation "array": ${tags}`); }

    else if(!ValidationUtils.stringAllowEmpty(title))
    { throw Error(`Invalid property "title" does not pass validation "string": ${title}`); }
  }
}
export default Image;
